import { useEffect, useState, useCallback, useMemo } from "react"
import { useAppSelector, useAppDispatch } from '../hooks/reduxToolkit'
import { useAsyncAction } from "../hooks/useAsyncAction"
import { BlobRow } from "../../types/supabase"
import { useClient } from "../providers/supabase"
import { createAttachment } from "../reduxToolkit/attachmentsSlice"
import { v4 as uuidv4 } from 'uuid'
import * as bootstrap from "bootstrap"
import usePortal from "react-useportal"
import { useReceiptSearch } from "../hooks/useReceiptSearch"
import { extractExpenseFromTextract } from "../../lib/util/extractExpenseFromTextract"
import { formatDateInTimeZone } from "../../lib/formatDateInTimeZone"
import { Tooltip } from "../components/tooltip"
import { present } from "../../lib/util/present"

import './receiptLibraryModal.scss'
import { BlobModel } from "../reduxToolkit/blobsSlice"

// Helper functions for Textract job status
function isBlobTextractPending(blob: BlobRow | BlobModel): boolean {
  return present(blob.textract_job_started_at) &&
    !present(blob.textract_job_completed_at)
}

// 10 minutes timeout (same as expense logic)
const TEXTRACT_TIMEOUT_MILLISECONDS = 10 * 60 * 1000
function isBlobTextractJobFailed(blob: BlobRow | BlobModel): boolean {
  if (!present(blob.textract_job_status)) { return false }
  if (blob.textract_job_status === 'FAILED') { return true }

  if (!present(blob.textract_job_completed_at) && present(blob.textract_job_started_at)) {
    return Date.parse(blob.textract_job_started_at) < (Date.now() - TEXTRACT_TIMEOUT_MILLISECONDS)
  }
  return false
}

function getBlobErrorMessage(blob: BlobRow | BlobModel): string | null {
  try {
    const results = typeof blob.textract_job_results === 'string'
      ? JSON.parse(blob.textract_job_results)
      : blob.textract_job_results

    return results?.error || null
  } catch {
    return null
  }
}

interface ReceiptLibraryModalProps {
  isOpen: boolean
  onClose: () => void
  onAttached: (blobs: BlobModel[]) => void
  expenseId: string
  initialFilter?: 'all' | 'receipt' | 'itemized-bill'
}

export function ReceiptLibraryModal({ isOpen, onClose, onAttached, expenseId, initialFilter = 'all' }: ReceiptLibraryModalProps) {
  const { Portal } = usePortal()
  const dispatch = useAppDispatch()
  const attachments = useAppSelector((s) => s.attachments.attachments)
  const membershipId = useAppSelector((s) => s.membership.membershipId)
  const allBlobs = useAppSelector((s) => s.blobs.blobs)

  const [selectedBlobs, setSelectedBlobs] = useState<Set<string>>(new Set())
  const [filterType, setFilterType] = useState<'all' | 'receipt' | 'itemized-bill'>('all')
  const [attachAsPurpose, setAttachAsPurpose] = useState<'receipt' | 'itemized-bill'>('receipt')
  const [hasUserChangedAttachAsPurpose, setHasUserChangedAttachAsPurpose] = useState<boolean>(false)

  const uniqueId = useMemo<number>(() => Math.floor(Math.random() * 10_000), [])
  const domId = `receipt-library-modal-${uniqueId}`

  // Get all blobs from Redux that are receipt library eligible and have no non-message attachments
  const blobs = useMemo(() => {
    // Filter out blobs that have non-message attachments (message attachments from email import don't count)
    const attachedBlobKeys = new Set(
      attachments
        .filter((a: any) => a.table_name !== 'messages')
        .map((a: any) => a.blob_key)
    )

    return allBlobs
      .filter((blob: any) =>
        blob.is_receipt_library_eligible &&
        !blob.deleted_at &&
        !attachedBlobKeys.has(blob.key)
      )
      .sort((a: any, b: any) => b.created_at.localeCompare(a.created_at))
  }, [allBlobs, attachments])

  // Search functionality
  const [
    searchResults,
    search,
    { query }
  ] = useReceiptSearch(blobs || [])

  // Filter receipts by type and combine with search results
  const getFilteredResults = () => {
    const baseResults = searchResults || blobs || []

    if (filterType === 'all') {
      return baseResults
    }

    return baseResults.filter((blob: any) =>
      blob.blob_original_purpose === filterType
    )
  }

  const [attachBlobsState, attachBlobs] = useAsyncAction(async () => {
    const selectedBlobsList = blobs?.filter((blob: any) => selectedBlobs.has(blob.key)) || []
    
    // Create attachments for selected blobs
    const attachmentPromises = selectedBlobsList.map(blob => {
      const attachmentId = uuidv4()
      const now = new Date().toISOString()
      
      return dispatch(createAttachment({
        id: attachmentId,
        record_id: expenseId,
        table_name: 'expenses',
        blob_key: blob.key,
        membership_id: membershipId,
        updated_at: now,
        created_at: now,
        purpose: attachAsPurpose,
      }))
    })
    
    await Promise.all(attachmentPromises)
    
    onAttached(selectedBlobsList)
    setSelectedBlobs(new Set())
    setHasUserChangedAttachAsPurpose(false)
    setAttachAsPurpose('receipt')
    onClose()
  }, [selectedBlobs, blobs, expenseId, membershipId, onAttached, onClose, attachAsPurpose, dispatch])

  const showModal = useCallback(() => {
    const modalElem = document.getElementById(domId)
    const modal = bootstrap.Modal.getInstance(modalElem!) || new bootstrap.Modal(modalElem!, {
      backdrop: 'static',
      keyboard: false,
    })
    modal?.show()
  }, [domId])
  
  const hideModal = useCallback(() => {
    setSelectedBlobs(new Set())
    setHasUserChangedAttachAsPurpose(false)
    setAttachAsPurpose('receipt')
    const modalElem = document.getElementById(domId)
    const modal = bootstrap.Modal.getInstance(modalElem!)
    modal?.hide()
  }, [domId])

  useEffect(() => {
    if (isOpen) {
      showModal()
      // Set the initial filter when modal opens
      setFilterType(initialFilter)
    } else {
      hideModal()
    }
  }, [isOpen, showModal, hideModal, initialFilter])

  const toggleSelection = useCallback((blobKey: string) => {
    setSelectedBlobs(prev => {
      const newSet = new Set(prev)
      if (newSet.has(blobKey)) {
        newSet.delete(blobKey)
      } else {
        newSet.add(blobKey)
      }

      return newSet
    })
  }, [])

  // Check if there's a classification mismatch warning to show
  const getClassificationWarning = useCallback(() => {
    if (selectedBlobs.size === 0) return null

    // Get all selected blobs
    const selectedBlobsList = (blobs || []).filter(blob => selectedBlobs.has(blob.key))

    // Check if any selected blob has a classification mismatch
    for (const blob of selectedBlobsList) {
      if (!blob.textract_job_results) continue

      const results = blob.textract_job_results as any
      if (results.document_classification?.type && results.document_classification?.reason) {
        const claudeClassification = results.document_classification.type
        const claudeReason = results.document_classification.reason

        // Show warning if Claude says it's not an itemized bill but user wants to attach as itemized bill
        if (claudeClassification !== 'itemized-bill' && attachAsPurpose === 'itemized-bill') {
          return {
            type: 'warning',
            message: selectedBlobsList.length === 1
              ? `This doesn't look like an itemized bill! ${claudeReason}`
              : `Some selected items don't look like itemized bills! Please review your selection.`
          }
        }
      }
    }

    return null
  }, [selectedBlobs, attachAsPurpose, blobs])

  // Update attach as purpose when selection changes
  useEffect(() => {
    if (!hasUserChangedAttachAsPurpose) {
      if (selectedBlobs.size === 1) {
        const firstKey = Array.from(selectedBlobs)[0]
        const candidate = (blobs || []).find((b) => b.key === firstKey)
        if (candidate?.blob_original_purpose === 'itemized-bill') {
          setAttachAsPurpose('itemized-bill')
        } else {
          setAttachAsPurpose('receipt')
        }
      } else if (selectedBlobs.size === 0) {
        setHasUserChangedAttachAsPurpose(false)
        setAttachAsPurpose('receipt')
      }
    }
  }, [selectedBlobs, hasUserChangedAttachAsPurpose, blobs])

  const handleAttach = useCallback(() => {
    if (selectedBlobs.size > 0) {
      attachBlobs()
    }
  }, [selectedBlobs.size, attachBlobs])

  return (
    <Portal>
      <div className="modal receipt-library-modal fade" id={domId} aria-hidden="true">
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h2 className="modal-title fs-5">Attach from your attachments</h2>
              <button 
                type="button" 
                className="btn-close"
                aria-label="Close"
                onClick={onClose}
              />
            </div>
            <div className="modal-body">
              {blobs && (
                <>
                  {blobs.length === 0 ? (
                    <div className="empty-state text-center py-4">
                      <i className="material-icons mb-3" style={{ fontSize: '48px', color: 'var(--bs-gray-400)' }}>receipt</i>
                      <h4>No attachments available</h4>
                      <p className="text-muted">
                        Take photos of bills and receipts to add them to your attachments.
                      </p>
                    </div>
                  ) : (
                    <>
                      <div className="mb-3">
                        <div className="row">
                          <div className="col-md-6">
                            <label className="form-label"><strong>Search Attachments</strong></label>
                            <input 
                              className="form-control" 
                              type="search" 
                              placeholder="Provider, document, or amount" 
                              aria-label="Search attachments"
                              value={query || ''}
                              onChange={(e) => { e.preventDefault(); search(e.target.value) }} 
                            />
                          </div>
                          <div className="col-md-3 mt-1">
                            <label className="form-label"><strong>Filter</strong></label>
                            <select 
                              className="form-select" 
                              value={filterType} 
                              onChange={(e) => setFilterType(e.target.value as 'all' | 'receipt' | 'itemized-bill')}
                              aria-label="Filter attachments by type"
                            >
                              <option value="all">All Items</option>
                              <option value="receipt">Receipts</option>
                              <option value="itemized-bill">Itemized Bills</option>
                            </select>
                          </div>
                          <div className="col-md-3 mt-1">
                            <label className="form-label"><strong>Attach as</strong></label>
                            <select 
                              className="form-select" 
                              value={attachAsPurpose}
                              onChange={(e) => { setAttachAsPurpose(e.target.value as 'receipt' | 'itemized-bill'); setHasUserChangedAttachAsPurpose(true) }}
                              aria-label="Attachment purpose"
                            >
                              <option value="receipt">Receipt</option>
                              <option value="itemized-bill">Itemized Bill</option>
                            </select>
                          </div>
                        </div>
                        <small className="text-muted mt-2 d-block">
                          Select one or more receipts to attach to this expense ({selectedBlobs.size} selected)
                          {(query || filterType !== 'all') && ` • ${getFilteredResults().length} result${getFilteredResults().length !== 1 ? 's' : ''}`}
                        </small>

                        {/* Show classification warning if there's a mismatch */}
                        {getClassificationWarning() && (
                          <div className="d-flex align-items-start gap-2 mt-3 text-warning">
                            <i className="material-icons" style={{ fontSize: '20px' }}>warning</i>
                            <div>{getClassificationWarning()!.message}</div>
                          </div>
                        )}
                      </div>
                      <div className="receipt-grid">
                        {isOpen && getFilteredResults().map((blob: any) => (
                          <ReceiptSelectionCard
                            key={blob.key}
                            blob={blob}
                            isSelected={selectedBlobs.has(blob.key)}
                            onToggle={toggleSelection}
                          />
                        ))}
                      </div>
                    </>
                  )}
                </>
              )}
              
              {attachBlobsState.error && (
                <div className="alert alert-danger mt-3">
                  Failed to attach receipts: {attachBlobsState.error.message}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={onClose}>
                Cancel
              </button>
              <button 
                type="button" 
                className="btn btn-primary"
                disabled={selectedBlobs.size === 0 || attachBlobsState.loading}
                onClick={handleAttach}
              >
                {attachBlobsState.loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Attaching...
                  </>
                ) : (
                  `Attach ${selectedBlobs.size} Item${selectedBlobs.size !== 1 ? 's' : ''}`
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Portal>
  )
}

interface ReceiptSelectionCardProps {
  blob: BlobRow
  isSelected: boolean
  onToggle: (blobKey: string) => void
}

function ReceiptSelectionCard({ blob, isSelected, onToggle }: ReceiptSelectionCardProps) {
  const client = useClient()
  const [imageUrl, setImageUrl] = useState<string>()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string>()

  useEffect(() => {
    let revoked = false
    let objectUrl: string | undefined

    const loadPreview = async () => {
      try {
        const download = await client.storage
          .from('wcc-hra-calculator-private')
          .download(blob.file_full_path)

        if (download.error) {
          throw new Error(download.error.message)
        }

        if (!download.data) {
          throw new Error('No data received')
        }

        if (revoked) return

        objectUrl = URL.createObjectURL(download.data)
        setImageUrl(objectUrl)
      } catch (err) {
        if (!revoked) {
          setError(err instanceof Error ? err.message : 'Failed to load preview')
        }
      } finally {
        if (!revoked) {
          setLoading(false)
        }
      }
    }

    loadPreview()

    return () => {
      revoked = true
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl)
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blob.file_full_path])

  const handleClick = useCallback(() => {
    onToggle(blob.key)
  }, [blob.key, onToggle])

  const getDisplayDate = (blob: BlobRow) => {
    const textractData = extractExpenseFromTextract(blob)
    if (textractData?.date) {
      return formatDateInTimeZone(textractData.date, { format: 'MMM dd, yyyy' })
    }
    return new Date(blob.created_at).toLocaleDateString()
  }

  const renderTextractDetails = (blob: BlobRow) => {
    const textractData = extractExpenseFromTextract(blob)
    if (!textractData) return null

    // Only show fields that aren't already displayed as primary info
    const hasProvider = textractData.provider
    const hasAmount = textractData.listedAmount || textractData.paidAmount

    if (!hasProvider && !hasAmount) return null

    return (
      <div className="receipt-textract-details">
        {hasProvider && (
          <div className="textract-field" title={`Provider: ${textractData.provider}`}>
            <i className="material-icons">business</i>
            <span>{textractData.provider}</span>
          </div>
        )}
        {hasAmount && (
          <div className="textract-field" title={`Amount: $${textractData.listedAmount || textractData.paidAmount}`}>
            <i className="material-icons">attach_money</i>
            <span>${textractData.listedAmount || textractData.paidAmount}</span>
          </div>
        )}
      </div>
    )
  }

  return (
    <div 
      className={`receipt-selection-card ${isSelected ? 'selected' : ''}`} 
      onClick={handleClick}
    >
      <div className="receipt-selection-checkbox">
        <input 
          type="checkbox" 
          className="form-check-input"
          checked={isSelected}
          onChange={() => {}} // Handled by card click
        />
      </div>
      
      <div className="receipt-card-preview">
        {loading && (
          <div className="d-flex justify-content-center align-items-center h-100">
            <div className="spinner-border spinner-border-sm" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        )}

        {error && (
          <div className="d-flex justify-content-center align-items-center h-100 text-danger">
            <i className="material-icons">error</i>
          </div>
        )}

        {imageUrl && !loading && (
          <>
            {blob.content_type === 'application/pdf' ? (
              <div className="pdf-preview">
                <i className="material-icons">picture_as_pdf</i>
                <span>PDF</span>
              </div>
            ) : (
              <img src={imageUrl} alt={blob.file_name} />
            )}
          </>
        )}

        {/* Status icons in top right corner */}
        <div className="receipt-status-icons">
          {isBlobTextractPending(blob) && !isBlobTextractJobFailed(blob) && (
            <Tooltip tooltip="Scanning receipt to extract information. This can sometimes take a few minutes.">
              <i className="material-icons flicker text-primary">sensors</i>
            </Tooltip>
          )}
          {isBlobTextractJobFailed(blob) && (
            <Tooltip tooltip={getBlobErrorMessage(blob) || "Unable to automatically extract receipt information"}>
              <i className="material-icons text-danger">error</i>
            </Tooltip>
          )}
        </div>
      </div>
      
      <div className="receipt-card-info">
        <div className="receipt-card-filename" title={blob.file_name}>
          {blob.file_name}
        </div>
        <div className="receipt-card-date text-muted">
          {getDisplayDate(blob)}
        </div>
        {renderTextractDetails(blob)}
      </div>
    </div>
  )
}